import axios from "axios";

const baseURL = process.env.REACT_APP_BACKEND_URL;

const axiosInstance = axios.create({ baseURL });

// Rutas de autenticación: sin Authorization y sin lógica de refresh. Un 401
// de token/refresh/ no debe intentar refrescar otra vez.
const AUTH_URLS = ["login/", "token/refresh/"];
const isAuthUrl = (url = "") => AUTH_URLS.some((u) => String(url).includes(u));

// Un solo refresh en vuelo. Los 401 que lleguen mientras tanto esperan a esta
// misma promesa y reintentan con el token nuevo, en vez de descartarse. Antes
// se rechazaban: con los polls en paralelo del detalle, cada vencimiento del
// access (15 min) dejaba una ráfaga de "Error fetching..." y el gráfico
// horario sin refrescar hasta la hora siguiente. En Safari, que dispara todos
// los timers juntos al volver a la pestaña, pasaba en cada regreso.
let refreshPromise = null;

function logout() {
  try {
    localStorage.clear();
  } catch (e) {
    // sin storage no hay nada que limpiar
  }
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

async function refreshAccessToken() {
  const refresh = localStorage.getItem("refresh");
  if (!refresh) {
    throw new Error("sin refresh token");
  }
  // axios "pelado", sin interceptores: un 401 aquí no debe volver a entrar.
  const response = await axios.post(`${baseURL}token/refresh/`, { refresh });
  const { access, refresh: rotated } = response.data || {};
  if (!access) {
    throw new Error("respuesta de refresh sin access");
  }
  localStorage.setItem("access", access);
  if (rotated) {
    localStorage.setItem("refresh", rotated);
  }
  return access;
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access");
    if (token && !isAuthUrl(config.url)) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    const lang = localStorage.getItem("pdui_language") || "es";
    config.headers["Accept-Language"] = lang;
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    if (!config || status !== 401 || isAuthUrl(config.url)) {
      return Promise.reject(error);
    }

    if (config._retried) {
      // El backend rechaza un access recién emitido (usuario desactivado o
      // borrado): la sesión no sirve. Sin esta guarda el interceptor
      // refrescaba y reintentaba sin fin.
      logout();
      return Promise.reject(error);
    }

    if (!localStorage.getItem("refresh")) {
      logout();
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const access = await refreshPromise;
      config._retried = true;
      config.headers["Authorization"] = `Bearer ${access}`;
      return axiosInstance(config);
    } catch (err) {
      // Refresh vencido (7 días) o inválido: cerrar sesión limpiamente.
      logout();
      return Promise.reject(error);
    }
  },
);

export default axiosInstance;
